import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LuPencil, LuLock, LuCircleHelp, LuMessageSquare,
  LuChevronRight, LuChevronDown, LuBell, LuMail, LuArrowLeft,
} from 'react-icons/lu';
import TraderLayout from '../../components/TraderLayout/TraderLayout';
import {
  fetchCurrentUser, updateCurrentUser, getAssetUrl,
} from '../../lib/directus';
import styles from './UserProfile.module.css';

export default function UserProfile() {
  const navigate = useNavigate();
  const [user, setUser]     = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit profile
  const [editMode, setEditMode]   = useState(false);
  const [editFirst, setEditFirst] = useState('');
  const [editLast, setEditLast]   = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [saving, setSaving]       = useState(false);

  // Change password
  const [pwOpen, setPwOpen]       = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwError, setPwError]     = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwSaving, setPwSaving]   = useState(false);

  // Preferences
  const [pushNotif, setPushNotif]   = useState(() => localStorage.getItem('pref_push') !== 'false');
  const [emailNotif, setEmailNotif] = useState(() => localStorage.getItem('pref_email') === 'true');

  const loadUser = useCallback(() => {
    setLoading(true);
    fetchCurrentUser()
      .then(setUser)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  function startEdit() {
    setEditFirst(user?.first_name || '');
    setEditLast(user?.last_name || '');
    setEditEmail(user?.email || '');
    setEditMode(true);
  }

  async function handleSaveProfile() {
    setSaving(true);
    try {
      const updated = await updateCurrentUser({
        first_name: editFirst.trim(),
        last_name:  editLast.trim(),
        email:      editEmail.trim(),
      });
      setUser(prev => ({ ...prev, ...updated }));
      setEditMode(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword() {
    if (!newPw)              { setPwError('Enter a new password'); return; }
    if (newPw !== confirmPw) { setPwError('Passwords do not match'); return; }
    if (newPw.length < 8)    { setPwError('Password must be at least 8 characters'); return; }
    setPwError('');
    setPwSaving(true);
    try {
      await updateCurrentUser({ password: newPw });
      setPwSuccess(true);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => { setPwSuccess(false); setPwOpen(false); }, 2000);
    } catch (err) {
      setPwError(err.message || 'Failed to update password');
    } finally {
      setPwSaving(false);
    }
  }

  function handlePushToggle(val) {
    setPushNotif(val);
    localStorage.setItem('pref_push', String(val));
    updateCurrentUser({ push_notifications: val }).catch(() => {});
  }

  function handleEmailToggle(val) {
    setEmailNotif(val);
    localStorage.setItem('pref_email', String(val));
    updateCurrentUser({ email_notifications: val }).catch(() => {});
  }

  const displayName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || '—'
    : '—';
  const avatarSrc = user?.avatar ? getAssetUrl(user.avatar) : '/avatar-holder-image.jpg';

  return (
    <TraderLayout>
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <LuArrowLeft size={16} />
          Back
        </button>
        <div className={styles.topRow}>

          {/* Profile card */}
          <div className={styles.profileCard}>
            <img src={avatarSrc} alt="Avatar" className={styles.avatarImg} />
            {editMode ? (
              <>
                <input
                  className={styles.profileInput}
                  placeholder="First name"
                  value={editFirst}
                  onChange={e => setEditFirst(e.target.value)}
                />
                <input
                  className={styles.profileInput}
                  placeholder="Last name"
                  value={editLast}
                  onChange={e => setEditLast(e.target.value)}
                />
                <input
                  className={styles.profileInput}
                  placeholder="Email"
                  type="email"
                  value={editEmail}
                  onChange={e => setEditEmail(e.target.value)}
                />
                <div className={styles.profileEditActions}>
                  <button className={styles.btnSave} onClick={handleSaveProfile} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button className={styles.btnCancel} onClick={() => setEditMode(false)}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className={styles.profileName}>{loading ? '—' : displayName}</p>
                <p className={styles.profileEmail}>{loading ? '—' : (user?.email || '—')}</p>
                <button className={styles.editBtn} onClick={startEdit}>
                  <LuPencil size={13} />
                  Edit profile
                </button>
              </>
            )}
          </div>

          {/* Settings card */}
          <div className={styles.settingsCard}>
            <p className={styles.settingsSectionTitle}>Personal settings</p>

            <div className={styles.settingsList}>
              {/* Change password */}
              <div>
                <div
                  className={styles.settingsRow}
                  onClick={() => { setPwOpen(o => !o); setPwError(''); setPwSuccess(false); }}
                >
                  <div className={styles.settingsRowLeft}>
                    <LuLock size={16} color="#6b7280" />
                    <span className={styles.settingsRowText}>Change password</span>
                  </div>
                  {pwOpen
                    ? <LuChevronDown size={16} color="#9ca3af" />
                    : <LuChevronRight size={16} color="#9ca3af" />}
                </div>
                {pwOpen && (
                  <div className={styles.changePwSection}>
                    <input
                      type="password"
                      className={styles.changePwInput}
                      placeholder="Current password"
                      value={currentPw}
                      onChange={e => setCurrentPw(e.target.value)}
                    />
                    <input
                      type="password"
                      className={styles.changePwInput}
                      placeholder="New password"
                      value={newPw}
                      onChange={e => setNewPw(e.target.value)}
                    />
                    <input
                      type="password"
                      className={styles.changePwInput}
                      placeholder="Confirm new password"
                      value={confirmPw}
                      onChange={e => setConfirmPw(e.target.value)}
                    />
                    {pwError   && <p className={styles.msgError}>{pwError}</p>}
                    {pwSuccess && <p className={styles.msgSuccess}>Password updated successfully!</p>}
                    <div className={styles.changePwActions}>
                      <button
                        className={styles.btnSave}
                        style={{ flex: 'none', padding: '7px 20px' }}
                        onClick={handleChangePassword}
                        disabled={pwSaving}
                      >
                        {pwSaving ? 'Saving…' : 'Save changes'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Help center */}
              <div
                className={styles.settingsRow}
                onClick={() => window.open('https://driftwoodagri.com/help', '_blank')}
              >
                <div className={styles.settingsRowLeft}>
                  <LuCircleHelp size={16} color="#6b7280" />
                  <span className={styles.settingsRowText}>Help center</span>
                </div>
                <LuChevronRight size={16} color="#9ca3af" />
              </div>

              {/* Send us a message */}
              <div
                className={styles.settingsRow}
                style={{ borderBottom: 'none' }}
                onClick={() => { window.location.href = 'mailto:support@driftwoodagri.com'; }}
              >
                <div className={styles.settingsRowLeft}>
                  <LuMessageSquare size={16} color="#6b7280" />
                  <span className={styles.settingsRowText}>Send us a message</span>
                </div>
                <LuChevronRight size={16} color="#9ca3af" />
              </div>
            </div>

            <p className={styles.settingsSectionTitle} style={{ marginTop: 20 }}>Preferences</p>

            <div className={styles.settingsList}>
              <div className={styles.settingsRow}>
                <div className={styles.settingsRowLeft}>
                  <LuBell size={16} color="#6b7280" />
                  <span className={styles.settingsRowText}>Push notifications</span>
                </div>
                <label className={styles.toggle} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className={styles.toggleInput}
                    checked={pushNotif}
                    onChange={e => handlePushToggle(e.target.checked)}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
              <div className={styles.settingsRow} style={{ borderBottom: 'none' }}>
                <div className={styles.settingsRowLeft}>
                  <LuMail size={16} color="#6b7280" />
                  <span className={styles.settingsRowText}>Email notification</span>
                </div>
                <label className={styles.toggle} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className={styles.toggleInput}
                    checked={emailNotif}
                    onChange={e => handleEmailToggle(e.target.checked)}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TraderLayout>
  );
}
